import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import {
  fetchDeployments,
  fetchDeployment,
  fetchLatestDeployment,
  fetchAllLatestDeployments,
  createDeployment,
  updateDeployment,
} from "../actions/deploymentActions";

// Deployment interfaces
export interface UnrealAppDeployment {
  id: string;
  organization_id: string;
  created_at: string;
  build_id: string;
  supplanted_unreal_app_deployment_id?: string;
  behavior_id?: string; // Deprecated - use behavior_config_id instead
  behavior_config_id: string;
  created_by_user_id?: string;
  min_hosts?: number;
  max_hosts?: number;
  is_testbed_deployment?: boolean;
  disable_phantom_sessions?: boolean;
}

// State interface
interface DeploymentState {
  deployments: UnrealAppDeployment[];
  currentDeployment: UnrealAppDeployment | null;
  latestDeployment: UnrealAppDeployment | null;
  latestDeployments: Record<string, UnrealAppDeployment | null>;
  loading: boolean;
  error: string | null;
}

// Initial state
const initialState: DeploymentState = {
  deployments: [],
  currentDeployment: null,
  latestDeployment: null,
  latestDeployments: {},
  loading: false,
  error: null,
};

// Create the slice
const deploymentSlice = createSlice({
  name: "deployments",
  initialState,
  reducers: {
    clearCurrentDeployment(state) {
      state.currentDeployment = null;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<DeploymentState>) => {
    // Handle fetchDeployments
    builder
      .addCase(fetchDeployments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDeployments.fulfilled,
        (state, action: PayloadAction<UnrealAppDeployment[]>) => {
          state.loading = false;
          state.deployments = action.payload;
        }
      )
      .addCase(fetchDeployments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch deployments";
      });

    // Handle fetchDeployment
    builder
      .addCase(fetchDeployment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchDeployment.fulfilled,
        (state, action: PayloadAction<UnrealAppDeployment>) => {
          state.loading = false;
          state.currentDeployment = action.payload;
        }
      )
      .addCase(fetchDeployment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch deployment";
      });

    // Handle fetchLatestDeployment
    builder
      .addCase(fetchLatestDeployment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchLatestDeployment.fulfilled,
        (state, action: PayloadAction<UnrealAppDeployment>) => {
          state.loading = false;
          state.latestDeployment = action.payload;
        }
      )
      .addCase(fetchLatestDeployment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch latest deployment";
      });

    // Handle fetchAllLatestDeployments
    builder
      .addCase(fetchAllLatestDeployments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchAllLatestDeployments.fulfilled,
        (state, action: PayloadAction<Record<string, UnrealAppDeployment | null>>) => {
          state.loading = false;
          state.latestDeployments = action.payload;
        }
      )
      .addCase(fetchAllLatestDeployments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch all latest deployments";
      });

    // Handle createDeployment
    builder
      .addCase(createDeployment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createDeployment.fulfilled,
        (state, action: PayloadAction<UnrealAppDeployment>) => {
          state.loading = false;
          // Add the new deployment at the beginning since it's the newest
          state.deployments.unshift(action.payload);
          state.currentDeployment = action.payload;
          state.latestDeployment = action.payload;
        }
      )
      .addCase(createDeployment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to create deployment";
      });

    // Handle updateDeployment
    builder
      .addCase(updateDeployment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateDeployment.fulfilled,
        (state, action: PayloadAction<UnrealAppDeployment>) => {
          state.loading = false;
          const updatedDeployment = action.payload;

          // Add the new deployment to the beginning of the array since it's the newest
          state.deployments.unshift(updatedDeployment);

          // Update currentDeployment if it's the same organization
          if (state.currentDeployment?.organization_id === updatedDeployment.organization_id) {
            state.currentDeployment = updatedDeployment;
          }

          // Update latestDeployment if it's the same organization
          if (state.latestDeployment?.organization_id === updatedDeployment.organization_id) {
            state.latestDeployment = updatedDeployment;
          }
        }
      )
      .addCase(updateDeployment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to update deployment";
      });
  },
});

export const { clearCurrentDeployment } = deploymentSlice.actions;
export default deploymentSlice.reducer;
